import React, { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HeimdallContext from "../HeimdallContext";
import { UserData } from "../context/auth";
import { faClock, faCogs, faCubes, faDatabase, faEnvelope, faSignIn, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Box, Fade, List, ListItemButton, Typography, useTheme } from "@mui/material";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";

const translations = defineMessages({
    account: {
        id: "sidebar_menu.account",
        defaultMessage: "account"
    },
    general: {
        id: "sidebar_menu.general",
        defaultMessage: "General"
    },
    emails: {
        id: "sidebar_menu.emails",
        defaultMessage: "Emails"
    },
    aliases: {
        id: "sidebar_menu.aliases",
        defaultMessage: "Login names"
    },
    sessions: {
        id: "sidebar_menu.sessions",
        defaultMessage: "$user.sessions"
    },
    admin: {
        id: "sidebar_menu.admin",
        defaultMessage: "administration"
    },
    clients: {
        id: "sidebar_menu.clients",
        defaultMessage: "Clients"
    },
    users: {
        id: "sidebar_menu.users",
        defaultMessage: "Users"
    },
    audit: {
        id: "sidebar_menu.audit",
        defaultMessage: "Audit log"
    },
    ldap: {
        id: "sidebar_menu.ldap",
        defaultMessage: "LDAP"
    }
})

const arrowSize = 10;
const itemPadding = 16;

const SectionTitle = ({ label }: { label: MessageDescriptor }) => (
    <Typography sx={{ textTransform: "uppercase" }}>
        <FormattedMessage {...label} />
    </Typography>
);

type SidebarProps = {
    context: HeimdallContext;
    userData: UserData;
};

const SidebarItem = ({
    label,
    path,
    icon,
    iconColor,
}: {
    label: MessageDescriptor;
    path: string;
    icon: IconDefinition;
    iconColor: string;
}) => {
    const location = useLocation();
    const theme = useTheme();
    const active = location.pathname.startsWith(path);
    return (
        <ListItemButton
            selected={active}
            component={Link}
            to={{ pathname: path }}
            style={{
                paddingLeft: 0,
                paddingRight: itemPadding,
            }}
        >
            <Box style={{ display: "flex", width: "100%", alignItems: "center" }}>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                        <FontAwesomeIcon icon={icon} color={iconColor} />
                    </Box>
                    <Box sx={{ marginLeft: 2 }}>
                        <FormattedMessage {...label} />
                    </Box>
                </Box>
                <Fade in={active}>
                    <div
                        style={{
                            borderTop: `${arrowSize}px solid transparent`,
                            borderBottom: `${arrowSize}px solid transparent`,
                            borderRight: `${arrowSize}px solid ${theme.palette.background.default}`,
                            height: arrowSize,
                            marginRight: -itemPadding,
                        }}
                    />
                </Fade>
            </Box>
        </ListItemButton>
    );
};

export const Sidebar = ({ context, userData }: SidebarProps) => {
    const sidebarItems: ReactNode[] = [];
    const userListUrl = context.isInternalIdenityProvider ? "users" : "user_subs";

    if (context.isInternalIdenityProvider) {
        sidebarItems.push(<SectionTitle key="sidebar_menu.account" label={translations.account} />);
        if (userData.userId)
            sidebarItems.push(
                <SidebarItem
                    key="sidebar_menu.general"
                    label={translations.general}
                    path="/account/general"
                    icon={faCogs}
                    iconColor="cyan"
                />,
                <SidebarItem
                    key="sidebar_menu.emails"
                    label={translations.emails}
                    path="/account/emails"
                    icon={faEnvelope}
                    iconColor="cyan"
                />,
                <SidebarItem
                    key="sidebar_menu.aliases"
                    label={translations.aliases}
                    path="/account/aliases"
                    icon={faSignIn}
                    iconColor="cyan"
                />,
            );
        sidebarItems.push(
            <SidebarItem
                key="sidebar_menu.sessions"
                label={translations.sessions}
                path="/account/sessions"
                icon={faClock}
                iconColor="cyan"
            />,
        );
    }

    if (userData.hasPermission("system")) {
        sidebarItems.push(
            <SectionTitle key={"sidebar_menu.admin"} label={translations.admin} />,
            <SidebarItem
                key="sidebar_menu.clients"
                label={translations.clients}
                path="/clients/list"
                icon={faCubes}
                iconColor="lime"
            />,
            <SidebarItem
                key="sidebar_menu.users"
                label={translations.users}
                path={`/${userListUrl}/list`}
                icon={faUsers}
                iconColor="lime"
            />,
        );

        if (window.features.audit) {
            sidebarItems.push(
                <SidebarItem
                    key="sidebar_menu.audit"
                    label={translations.audit}
                    path={"/audit/list"}
                    icon={faClock}
                    iconColor="lime"
                />,
            );
        }

        if (global.identityProvider === "internal" && window.features.ldap)
            sidebarItems.push(
                <SidebarItem
                    key="sidebar_menu.ldap"
                    label={translations.ldap}
                    path="/ldap_auths/list"
                    icon={faDatabase}
                    iconColor="lime"
                />,
            );
    }

    return <List sx={{ paddingLeft: 2 }}>{...sidebarItems}</List>;
};
